import { z } from 'zod';
import { CENTRO_DE_CUSTO, FORMA_PAGAMENTO, SITUACAO_LANCAMENTO, TIPO_LANCAMENTO, } from '../dominio/index.js';
import { adicionarDias } from '../tempo/index.js';
import { zData } from './agenda.js';
import { zCompetencia, zId } from './primitivos.js';
/**
 * Os contratos do razão — `#v-fin` e `#m-lancamento`.
 *
 * Três regras atravessam este arquivo inteiro:
 *
 *   DINHEIRO   centavos, inteiro. Nunca float: R$ 0,10 + R$ 0,20 não pode
 *              virar R$ 0,30000000000000004 num extrato.
 *
 *   DATA       data de CALENDÁRIO da arena ("2026-09-07"), não instante.
 *              O aluguel vence no dia 7 em Goiânia, e continua vencendo no
 *              dia 7 com o servidor em UTC e o navegador em Lisboa. O
 *              regime é de caixa: a data é a do movimento.
 *
 *   SINAL      `valor` é sempre positivo, e o `tipo` diz se entra ou sai.
 *              A única exceção é o ESTORNO: ele repete tipo e categoria do
 *              original com o valor negativo. É o que faz toda soma — por
 *              semana, por categoria, o saldo — já sair líquida, sem que
 *              cada agregado precise lembrar de descontar estornos.
 *
 * Ver: Arena_backend/Docs/plano/11-financeiro-razao.md
 */
// ---------------------------------------------------------------------
// Vocabulário
// ---------------------------------------------------------------------
/**
 * "Mensal", "Não se repete", "Semanal" — o seletor de `#m-lancamento`.
 * "Não se repete" é a AUSÊNCIA de recorrência, não um terceiro valor.
 */
export const FREQUENCIA_RECORRENCIA = ['Mensal', 'Semanal'];
/**
 * Quantas ocorrências de uma recorrência existem como linha à frente de
 * hoje: doze meses na mensal, doze semanas na semanal.
 *
 * O toast do protótipo é a especificação: *"Lançamento salvo e replicado
 * nos próximos 12 meses."* As ocorrências são MATERIALIZADAS, como as
 * séries da agenda — é o que permite realizar, cancelar ou corrigir uma
 * delas sem mexer nas outras. O resto do futuro é materializado aos
 * poucos pelo job do documento 07.
 */
export const HORIZONTE_DA_RECORRENCIA = 12;
/** O fato que gerou um lançamento automático. Ver `registrarLancamento`. */
export const ORIGEM_LANCAMENTO = ['Fatura', 'Comanda', 'EntradaMercadoria', 'Marcacao'];
export const ROTULO_TIPO_LANCAMENTO = {
    Entrada: 'Entrada',
    Saida: 'Saída',
};
export const ROTULO_CENTRO_DE_CUSTO = {
    Geral: 'Geral',
    Quadra1: 'Quadra 1',
    Quadra2: 'Quadra 2',
    Bar: 'Bar',
};
export const ROTULO_FORMA_PAGAMENTO = {
    Pix: 'Pix',
    Dinheiro: 'Dinheiro',
    Cartao: 'Cartão',
    Boleto: 'Boleto',
    Mensalidade: 'Mensalidade',
};
export const ROTULO_FREQUENCIA = {
    Mensal: 'Mensal',
    Semanal: 'Semanal',
};
/**
 * As quatro formas que `#m-lancamento` oferece. "Mensalidade" existe no
 * vocabulário porque a comanda pode ir para a fatura do aluno — é forma
 * de lançamento AUTOMÁTICO, não de digitação.
 */
export const FORMAS_DO_LANCAMENTO_MANUAL = [
    'Pix',
    'Boleto',
    'Dinheiro',
    'Cartao',
];
/**
 * "Somente esta" ou "esta e as seguintes" — a mesma pergunta da agenda,
 * feita ao cancelar uma ocorrência prevista de uma recorrência.
 */
export const ESCOPOS_DE_CANCELAMENTO = ['ocorrencia', 'recorrencia'];
// ---------------------------------------------------------------------
// Anexos — "Anexar comprovante" de #m-lancamento
// ---------------------------------------------------------------------
/**
 * Comprovante é PDF ou foto. A lista é curta de propósito: tudo que o
 * bucket aceita alguém vai abrir no navegador depois, e HTML ou SVG
 * "comprovante" é script esperando uma aba.
 */
export const TIPOS_DE_ANEXO = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
/** 10 MB: a foto de um boleto no celular tem 3 ou 4. */
export const TAMANHO_MAXIMO_DO_ANEXO = 10 * 1024 * 1024;
export function ehTipoDeAnexo(tipo) {
    return TIPOS_DE_ANEXO.includes(tipo);
}
// ---------------------------------------------------------------------
// Peças
// ---------------------------------------------------------------------
/**
 * O teto de um lançamento: R$ 10 milhões. A coluna é `INTEGER` (até
 * R$ 21 milhões em centavos); o teto deixa folga e pega o zero a mais
 * digitado por engano antes de ele virar o saldo do mês.
 */
export const VALOR_MAXIMO_DO_LANCAMENTO = 1_000_000_000;
export const zValorDoLancamento = z
    .number({ invalid_type_error: 'Informe o valor em centavos.' })
    .int('O valor é em centavos, sem fração.')
    .positive('O valor precisa ser maior que zero.')
    .max(VALOR_MAXIMO_DO_LANCAMENTO, 'Valor acima de R$ 10 milhões — confira os zeros.');
/** "2026-09" — o mês do extrato. A competência aceita "2026-13"; o mês, não. */
export const zMes = zCompetencia.refine((mes) => {
    const numero = Number(mes.slice(5, 7));
    return numero >= 1 && numero <= 12;
}, 'Mês inválido.');
const texto = (maximo) => z.string().trim().max(maximo, `No máximo ${maximo} caracteres.`);
/**
 * O detalhamento da folha: quanto cada professor recebe dentro do
 * lançamento. É a informação mais restrita do sistema — a matriz dá
 * "Total" ao Dono, "Próprio pagamento" ao Professor e "Não" à Gestão.
 *
 * O professor é referenciado pela CONTA dele (`usuario_id` de quem tem
 * membership na arena), porque é pela conta que "o próprio pagamento" é
 * reconhecido. Ver a decisão em 11-financeiro-razao.md e a migration.
 */
export const zItemDaFolha = z.object({
    professorUsuarioId: zId,
    valor: zValorDoLancamento,
    observacao: texto(200).optional(),
});
export const zFolhaDoLancamento = z
    .array(zItemDaFolha)
    .min(1, 'Informe ao menos um professor.')
    .max(50)
    .refine((itens) => new Set(itens.map((i) => i.professorUsuarioId)).size === itens.length, 'Cada professor aparece uma vez no detalhamento.');
// ---------------------------------------------------------------------
// Entradas
// ---------------------------------------------------------------------
/**
 * O corpo de `POST /financeiro/lancamentos` — os campos de
 * `#m-lancamento`.
 *
 * A descrição é opcional porque o protótipo não tem o campo: o
 * formulário dele pede categoria e subcategoria, e o extrato mostra
 * "Aluguel da arena", "Folha quinzenal dos professores". Sem descrição,
 * o servidor usa a subcategoria, ou o nome da categoria.
 *
 * O que depende do BANCO — a categoria é desta arena, é do mesmo tipo,
 * o perfil alcança a categoria, o realizado não está no futuro — é
 * conferido no servidor. Aqui fica o que o formulário também consegue
 * dizer sozinho.
 */
export const zNovoLancamento = z
    .object({
    tipo: z.enum(TIPO_LANCAMENTO, { errorMap: () => ({ message: 'Entrada ou saída.' }) }),
    categoriaId: zId,
    descricao: texto(120).optional(),
    subcategoria: texto(80).optional(),
    valor: zValorDoLancamento,
    data: zData,
    centroDeCusto: z.enum(CENTRO_DE_CUSTO),
    formaPagamento: z.enum(FORMA_PAGAMENTO).optional(),
    situacao: z.enum(SITUACAO_LANCAMENTO),
    /** Ausente: "Não se repete". */
    recorrencia: z.enum(FREQUENCIA_RECORRENCIA).optional(),
    folha: zFolhaDoLancamento.optional(),
})
    .superRefine((l, ctx) => {
    if (!l.folha)
        return;
    if (l.tipo !== 'Saida') {
        ctx.addIssue({ code: 'custom', path: ['folha'], message: 'Folha de pagamento é saída.' });
    }
    // O detalhamento é de UM pagamento. A folha que se repete prevê o
    // total; quem recebe quanto se diz ao realizar cada ocorrência —
    // replicar o rateio de hoje nos próximos doze meses seria inventar
    // o pagamento de quem ainda nem deu aula.
    if (l.recorrencia) {
        ctx.addIssue({
            code: 'custom',
            path: ['folha'],
            message: 'O detalhamento vai em cada pagamento: lance a folha sem repetição, ' +
                'ou detalhe ao realizar cada ocorrência.',
        });
    }
    if (somaDaFolha(l.folha) !== l.valor) {
        ctx.addIssue({
            code: 'custom',
            path: ['folha'],
            message: 'A soma do detalhamento precisa ser igual ao valor do lançamento.',
        });
    }
});
/** Quanto o detalhamento soma — tem que bater com o valor do lançamento. */
export function somaDaFolha(itens) {
    return itens.reduce((total, item) => total + item.valor, 0);
}
/**
 * O corpo de `PATCH /financeiro/lancamentos/:id` — corrigir.
 *
 * O TIPO não muda: uma entrada que vira saída é outro fato, e o caminho
 * é estornar ou cancelar e lançar de novo. Valor e data só mudam
 * enquanto o lançamento é previsto — o servidor recusa no realizado.
 */
export const zAlteracaoDeLancamento = z
    .object({
    categoriaId: zId.optional(),
    descricao: texto(120).min(1, 'A descrição não pode ficar vazia.').optional(),
    subcategoria: texto(80).nullable().optional(),
    valor: zValorDoLancamento.optional(),
    data: zData.optional(),
    centroDeCusto: z.enum(CENTRO_DE_CUSTO).optional(),
    formaPagamento: z.enum(FORMA_PAGAMENTO).nullable().optional(),
})
    .refine((a) => Object.keys(a).length > 0, { message: 'Nada para alterar.' });
/**
 * `Previsto` → `Realizado`, por ação explícita — é o que separa a
 * projeção do fato. O aluguel previsto para o dia 7 pode ter sido pago
 * no dia 8, e a conta de energia prevista em R$ 2.680 pode ter vindo
 * R$ 2.713: data, valor e forma do fato podem diferir da previsão.
 *
 * Na folha, é aqui que se diz quem recebeu quanto — a soma precisa bater
 * com o valor realizado, e o servidor confere.
 */
export const zRealizacaoDeLancamento = z.object({
    data: zData.optional(),
    valor: zValorDoLancamento.optional(),
    formaPagamento: z.enum(FORMA_PAGAMENTO).optional(),
    folha: zFolhaDoLancamento.optional(),
});
/** Estorno é lançamento contrário, não `DELETE` — e sempre diz por quê. */
export const zEstornoDeLancamento = z.object({
    motivo: z.string().trim().min(3, 'Diga por que o lançamento está sendo estornado.').max(200),
});
/**
 * Cancelar é para o PREVISTO — a previsão que não vai acontecer. O fato
 * (realizado) não se cancela: estorna-se. A linha fica, com o motivo.
 */
export const zCancelamentoDeLancamento = z.object({
    motivo: z.string().trim().min(3, 'Diga por que a previsão foi cancelada.').max(200),
    escopo: z.enum(ESCOPOS_DE_CANCELAMENTO).optional(),
});
/**
 * A página do extrato. O cursor é opaco — posição de teclado, não id —
 * para que um id qualquer colado na URL não sirva de sonda de onde uma
 * linha fora do escopo do perfil está na ordem.
 */
export const zConsultaDoExtrato = z.object({
    mes: zMes.optional(),
    cursor: z.string().max(200).optional(),
    limite: z.coerce.number().int().min(1).max(100).default(25),
});
/** A página de `GET /financeiro/folha` — mesma forma do cursor, sem o mês. */
export const zConsultaDaFolha = z.object({
    cursor: z.string().max(200).optional(),
    limite: z.coerce.number().int().min(1).max(100).default(25),
});
/**
 * Pedido de URL de envio. Nome, tipo e tamanho vêm do navegador e são só
 * a INTENÇÃO: depois do envio, o servidor confere no Storage o que
 * realmente chegou antes de gravar o anexo.
 */
export const zPedidoDeEnvioDeAnexo = z.object({
    nome: z.string().trim().min(1, 'O arquivo precisa de nome.').max(200),
    tipo: z.string().refine(ehTipoDeAnexo, 'Envie PDF, JPG, PNG ou WebP.'),
    tamanho: z
        .number()
        .int()
        .min(1, 'O arquivo está vazio.')
        .max(TAMANHO_MAXIMO_DO_ANEXO, 'O arquivo passa de 10 MB.'),
});
/**
 * "Terminei de enviar." Sem caminho no corpo, de propósito: o servidor
 * RECONSTRÓI o caminho a partir da arena da sessão — um caminho vindo
 * do cliente seria o jeito de apontar para o arquivo de outra arena.
 */
export const zConfirmacaoDeAnexo = z.object({
    anexoId: zId,
    nome: z.string().trim().min(1).max(200),
});
// ---------------------------------------------------------------------
// Calendário do razão — funções puras sobre "AAAA-MM" e "AAAA-MM-DD"
// ---------------------------------------------------------------------
const dois = (n) => String(n).padStart(2, '0');
/** "2026-09-07" → "2026-09". */
export function mesDe(data) {
    return data.slice(0, 7);
}
/** Aritmética de mês: `adicionarMeses('2026-12', 1)` → '2027-01'. */
export function adicionarMeses(mes, meses) {
    const [ano, numero] = mes.split('-').map(Number);
    const indice = ano * 12 + (numero - 1) + meses;
    return `${Math.floor(indice / 12)}-${dois((indice % 12) + 1)}`;
}
/** O primeiro dia do mês e o primeiro do seguinte: `[inicio, fim)`. */
export function intervaloDoMes(mes) {
    return { inicio: `${mes}-01`, fim: `${adicionarMeses(mes, 1)}-01` };
}
/** Quantos dias o mês tem. */
export function diasNoMes(mes) {
    const { inicio, fim } = intervaloDoMes(mes);
    let dias = 0;
    for (let d = inicio; d < fim; d = adicionarDias(d, 1))
        dias += 1;
    return dias;
}
/**
 * As semanas de "Entradas e saídas por semana": blocos de sete dias a
 * partir do dia 1 — 1 a 7, 8 a 14, 15 a 21, 22 a 28 e o resto.
 *
 * Blocos do calendário do MÊS, e não semanas de segunda a domingo, por
 * dois motivos: o gráfico é "de setembro" (o protótipo diz), e semana
 * cortada ao meio na virada do mês faria a mesma semana aparecer em dois
 * gráficos com números diferentes.
 */
export function semanasDoMes(mes) {
    const total = diasNoMes(mes);
    const semanas = [];
    for (let primeiro = 1, n = 1; primeiro <= total; primeiro += 7, n += 1) {
        const ultimo = Math.min(primeiro + 6, total);
        semanas.push({
            rotulo: `Sem ${n}`,
            inicio: `${mes}-${dois(primeiro)}`,
            fim: `${mes}-${dois(ultimo)}`,
        });
    }
    return semanas;
}
/** O que o lançamento faz com o caixa: `+` entra, `−` sai. Estorno já vem com sinal trocado. */
export function efeitoNoCaixa(lancamento) {
    return lancamento.tipo === 'Entrada' ? lancamento.valor : -lancamento.valor;
}
//# sourceMappingURL=financeiro.js.map